import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { ArrowRight, Check, ExternalLink, Mail, ShieldCheck, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const SUPPORT_EMAIL = "hello@nuvrahq.com";

function buildMailto(email: string) {
  const subject = "Nuvra account deletion request";
  const body = [
    "Hello Nuvra Support,",
    "",
    "I am requesting deletion of my Nuvra account and associated personal data.",
    "",
    `Nuvra account email: ${email.trim()}`,
    "",
    "Please confirm when the deletion request has been completed.",
  ].join("\n");

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mailto = useMemo(() => buildMailto(email), [email]);
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validEmail) return;
    setSubmitted(true);
    window.location.href = mailto;
  }

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute left-1/2 top-[-18rem] h-[36rem] w-[48rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute right-[-12rem] top-[22rem] h-[24rem] w-[24rem] rounded-full bg-cyan-500/5 blur-3xl" />
      </div>

      <header className="relative border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="flex items-center gap-3" aria-label="Nuvra home">
            <img src="/nuvra-logo.png" alt="" className="h-8 w-8 rounded-lg" />
            <span className="text-base font-semibold tracking-tight">Nuvra</span>
          </Link>
          <Link
            to="/privacy"
            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Privacy
          </Link>
        </div>
      </header>

      <section className="relative mx-auto max-w-6xl px-5 pb-16 pt-14 sm:px-8 sm:pb-24 sm:pt-20">
        <div className="mx-auto max-w-3xl">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary">
            <ShieldCheck className="h-3.5 w-3.5" />
            Privacy & account control
          </div>

          <div className="space-y-5">
            <h1 className="max-w-2xl text-4xl font-semibold tracking-[-0.04em] sm:text-6xl">
              Delete your Nuvra account
            </h1>
            <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              You are in control of your Nuvra account and the personal data associated with it.
              Use this page to request permanent account deletion, even if you no longer have the
              Nuvra app installed.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[
              ["Account", "Your Nuvra account and authentication profile"],
              ["Workspace", "Projects and platform data associated with your account"],
              ["Personal data", "Personal information associated with the account"],
            ].map(([title, description]) => (
              <div key={title} className="rounded-2xl border border-border/70 bg-card/60 p-5 backdrop-blur-sm">
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Check className="h-4 w-4" />
                </div>
                <p className="text-sm font-semibold">{title}</p>
                <p className="mt-1.5 text-xs leading-5 text-muted-foreground">{description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-3xl border border-border/80 bg-card/80 p-6 shadow-2xl shadow-black/5 backdrop-blur-xl sm:p-8">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-500/10 text-red-500">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold tracking-tight">Request account deletion</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  Enter the email address used for your Nuvra account. We will open a prepared
                  deletion request addressed to Nuvra Support.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="mt-7 space-y-4">
              <label htmlFor="deletion-email" className="block text-sm font-medium">
                Nuvra account email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="deletion-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setSubmitted(false);
                  }}
                  placeholder="you@example.com"
                  className="h-12 w-full rounded-xl border border-border bg-background/80 pl-11 pr-4 text-base outline-none transition focus:border-primary/60 focus:ring-4 focus:ring-primary/10"
                />
              </div>

              <button
                type="submit"
                disabled={!validEmail}
                className="group flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-foreground px-5 text-sm font-semibold text-background transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitted ? "Opening your email client…" : "Request account deletion"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>

              <p className="text-center text-xs leading-5 text-muted-foreground">
                This opens your email client with a pre-addressed request to{" "}
                <a className="font-medium text-foreground underline underline-offset-2" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>.
              </p>
            </form>
          </div>

          <div className="mt-8 grid gap-6 border-t border-border/70 pt-8 sm:grid-cols-2">
            <div>
              <h2 className="text-sm font-semibold">What happens next?</h2>
              <ol className="mt-3 space-y-3 text-sm leading-6 text-muted-foreground">
                <li><span className="mr-2 font-medium text-foreground">01</span> We verify the request belongs to the account holder.</li>
                <li><span className="mr-2 font-medium text-foreground">02</span> We process the account and associated data for deletion.</li>
                <li><span className="mr-2 font-medium text-foreground">03</span> We confirm completion by email.</li>
              </ol>
            </div>
            <div>
              <h2 className="text-sm font-semibold">Data retention</h2>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                Nuvra deletes account data associated with an approved deletion request. Certain
                records may be retained where required for accounting, fraud prevention, security,
                or other legitimate legal obligations, as described in the{" "}
                <Link to="/privacy" className="font-medium text-foreground underline underline-offset-2">
                  Privacy Policy
                </Link>.
              </p>
              <p className="mt-3 text-xs leading-5 text-muted-foreground">
                Deletion requests are processed within the timeframe described by Nuvra's account
                deletion process, with confirmation sent when completed.
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span>Nuvra · AI-Native Software Creation OS</span>
            <span className="hidden sm:inline">•</span>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-1.5 hover:text-foreground">
              Contact support <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
